// Shared helpers for the packet-ledger "Merged" view (Diamonds / Colour
// Stones / Pearls). Mirrors the Gold Ledger merged view: same-day, same-party
// movements of the same kind collapse into one row showing the totals, and the
// original rows can be opened in a breakdown dialog.

// IST calendar-day key ('YYYY-MM-DD') used to group entries by day.
export function istDayKey(ts) {
  try { return new Date(ts).toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' }); }
  catch { return String(ts || '').slice(0, 10); }
}

const low = (s) => String(s || '').trim().toLowerCase();

// The counterparty a ledger row should merge against, or null when the row
// must NEVER be merged (packet-to-packet transfers / re-cuts / breaks stay
// individual because each is a distinct event).
export function ledgerPartyKey(e) {
  const t = e.type;
  if (t === 'transfer_in' || t === 'transfer_out') return null;
  if (t === 'receipt') {
    if (e.sub_type === 'goldsmith_return') {
      return e.goldsmith_id ? `gs:${e.goldsmith_id}` : (e.goldsmith_name ? `gsn:${low(e.goldsmith_name)}` : null);
    }
    return e.vendor_name ? `v:${low(e.vendor_name)}` : null; // vendor / stock purchase receipt
  }
  // issue-like rows
  if (e.issue_type === 'sale') return e.sale_party_name ? `sp:${low(e.sale_party_name)}` : null;
  if (e.issue_type === 'purchase_return' || e.issue_type === 'return_to_vendor') {
    return e.vendor_name ? `v:${low(e.vendor_name)}` : null;
  }
  return e.goldsmith_id ? `gs:${e.goldsmith_id}` : (e.goldsmith_name ? `gsn:${low(e.goldsmith_name)}` : null);
}

// Grouping key — same direction + same movement sub-type + same party + same
// IST day. Returns null for rows that must stay individual.
export function ledgerGroupKey(e) {
  const pk = ledgerPartyKey(e);
  if (!pk) return null;
  return `${e.type}|${e.issue_type || e.sub_type || ''}|${pk}|${istDayKey(e.timestamp)}`;
}

// Build merged groups from the ledger entries (which arrive oldest-first with
// running balances already attached). Groups are ordered by their LATEST entry
// so the balance shown (the group's closing balance) reads naturally in the
// chronological ledger. Non-mergeable rows become single-entry groups.
export function buildMergedLedgerGroups(entries) {
  const map = new Map();
  const order = [];
  for (const e of entries || []) {
    const gk = ledgerGroupKey(e);
    const key = gk || `single:${e.id}`;
    let g = map.get(key);
    if (!g) {
      g = {
        key, mergeable: !!gk, type: e.type, issue_type: e.issue_type || '', sub_type: e.sub_type || '',
        day: istDayKey(e.timestamp), entries: [], total_carats: 0, total_pieces: 0, total_value: 0, rep: e,
      };
      map.set(key, g);
      order.push(key);
    }
    g.entries.push(e);
    g.total_carats += Number(e.carats || 0);
    g.total_pieces += Number(e.pieces || 0);
    g.total_value += Number(e.value || 0);
    if (new Date(e.timestamp).getTime() >= new Date(g.rep.timestamp).getTime()) g.rep = e;
  }
  const groups = order.map((k) => map.get(k));
  groups.sort((a, b) => new Date(a.rep.timestamp).getTime() - new Date(b.rep.timestamp).getTime());
  return groups;
}

// Direction + counterparty label for a merged row / breakdown heading.
export function ledgerMergeHeading(e) {
  const t = e.type;
  if (t === 'receipt') {
    if (e.sub_type === 'goldsmith_return') return { verb: 'Return from', party: e.goldsmith_name || 'goldsmith' };
    return { verb: 'From', party: e.vendor_name || 'vendor' };
  }
  if (e.issue_type === 'sale') return { verb: 'Sale to', party: e.sale_party_name || 'party' };
  if (e.issue_type === 'purchase_return') return { verb: 'Purchase Return to', party: e.vendor_name || 'vendor' };
  if (e.issue_type === 'return_to_vendor') return { verb: 'Return to', party: e.vendor_name || 'vendor' };
  return { verb: 'To', party: e.goldsmith_name || 'goldsmith' };
}

// Friendly IST day label for the merged-row Date cell.
export function formatLedgerDay(dayKey) {
  try {
    return new Date(`${dayKey}T12:00:00Z`).toLocaleDateString('en-GB', {
      timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', year: 'numeric',
    });
  } catch { return dayKey; }
}
